// src/components/AdminDashboard.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography, Button, Card, CardContent, TextField, Stack, Snackbar, Alert, Chip,
  ThemeProvider, createTheme, CssBaseline, Divider, MenuItem, Select, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, InputLabel, FormControl, Tabs, Tab
} from "@mui/material";
import { Visibility, Delete, LocalPharmacy, NotificationsNone, DirectionsRun } from "@mui/icons-material";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import axios from "axios";
import MedicineMasterAdmin from "./MedicineMasterAdmin";
import AdminControlTower from "./AdminControlTower";
import AdminIncidentManager from "./AdminIncidentManager";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// eslint-disable-next-line no-unused-vars
function renderAssignmentHistory(history = []) {
  if (!history.length) return null;
  return (
    <>
      <Typography sx={{ mt: 1 }}><b>Delivery Timeline:</b></Typography>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {history.map((h, i) => (
          <li key={i}>
            {h.status.charAt(0).toUpperCase() + h.status.slice(1)}:
            &nbsp;{h.at ? new Date(h.at).toLocaleString() : "—"}
          </li>
        ))}
      </ul>
    </>
  );
}

// --- SUPPORT CHATS ADMIN PANEL COMPONENT ---
function SupportChatsAdminPanel() {
  const [chats, setChats] = useState([]);
  const [filter, setFilter] = useState("pending_admin"); // or "all", "closed"
  const [replyMsg, setReplyMsg] = useState("");
  const [chat, setChat] = useState(null);

  useEffect(() => {
    const fetchChats = () =>
      axios.get(`${API_BASE_URL}/api/support-chat/all?status=${filter !== "all" ? filter : ""}`)
        .then(res => setChats(res.data));
    fetchChats();
    const interval = setInterval(fetchChats, 3000);
    return () => clearInterval(interval);
  }, [filter]);

  const handleReply = async () => {
    await axios.post(`${API_BASE_URL}/api/support-chat/admin-reply`, { chatId: chat._id, text: replyMsg });
    setReplyMsg("");
    setChat(null);
  };

  const handleCloseChat = async (chatId) => {
    await axios.post(`${API_BASE_URL}/api/support-chat/close`, { chatId });
    setChat(null);
  };

  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" sx={{ color: "#FFD43B", mb: 2 }}>Support Chats</Typography>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant={filter === "pending_admin" ? "contained" : "outlined"} onClick={() => setFilter("pending_admin")}>Pending</Button>
        <Button variant={filter === "closed" ? "contained" : "outlined"} onClick={() => setFilter("closed")}>Closed</Button>
        <Button variant={filter === "all" ? "contained" : "outlined"} onClick={() => setFilter("all")}>All</Button>
      </Stack>
      <Stack spacing={2}>
        {chats.length === 0 && <Typography color="success.main">No chats!</Typography>}
        {chats.map((c) => (
          <Box key={c._id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2, mb: 2 }}>
            <Typography>From: {c.userId?.name} ({c.userId?.email || c.userId?.mobile})</Typography>
            <Typography>Order: {c.orderId?.id || c.orderId?._id}</Typography>
            <Typography>Status: <b>{c.status}</b></Typography>
            <Button variant="outlined" color="success" size="small" onClick={() => setChat(c)}>View & Reply</Button>
          </Box>
        ))}
      </Stack>
      <Dialog open={!!chat} onClose={() => setChat(null)}>
        <DialogTitle>
          <SupportAgentIcon sx={{ mb: "-5px", mr: 1 }} />
          Support Chat with {chat?.userId?.name}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mb: 2, maxHeight: 250, overflowY: 'auto' }}>
            {chat?.messages.map((msg, i) =>
              <Typography key={i} sx={{ color: msg.sender === "user" ? "#FFD43B" : (msg.sender === "admin" ? "#13C0A2" : "#fff") }}>
                <b>{msg.sender}:</b> {msg.text}
              </Typography>
            )}
          </Stack>
          <TextField
            label="Reply Message"
            fullWidth
            value={replyMsg}
            onChange={e => setReplyMsg(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <Stack direction="row" spacing={2}>
            <Button onClick={handleReply} variant="contained" disabled={!replyMsg.trim()}>Send Reply</Button>
            {chat?.status !== "closed" &&
              <Button onClick={() => handleCloseChat(chat._id)} variant="outlined" color="error">Close Chat</Button>
            }
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChat(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function DeliveryPartnerChatsAdminPanel() {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);

  useEffect(() => {
    const fetchChats = () => axios.get(`${API_BASE_URL}/api/admin/delivery-chats`)
      .then(res => setChats(res.data));
    fetchChats();
    const interval = setInterval(fetchChats, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h6" sx={{ color: "#FFD43B", mb: 2 }}>Delivery Partner Chats</Typography>
      <Stack spacing={2}>
        {chats.length === 0 && <Typography color="success.main">No chats yet!</Typography>}
        {chats.map((c) => (
          <Box key={c.orderId} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography><b>Order:</b> #{c.orderId?.slice(-5)}</Typography>
                <Typography><b>Customer:</b> {c.customer}</Typography>
                <Typography><b>Partner:</b> {c.deliveryPartner}</Typography>
                <Typography variant="body2" sx={{ color: "#FFD43B" }}>
                  {new Date(c.orderDate).toLocaleString()}
                </Typography>
              </Box>
              <Button variant="outlined" color="primary" size="small" onClick={() => setSelectedChat(c)}>View Chat</Button>
            </Stack>
          </Box>
        ))}
      </Stack>
      <Dialog open={!!selectedChat} onClose={() => setSelectedChat(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delivery Partner Chat</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mb: 2, maxHeight: 320, overflowY: "auto" }}>
            {selectedChat?.messages.map((msg, i) => (
              <Typography
                key={i}
                sx={{
                  color: msg.senderType === "user" ? "#FFD43B" :
                    msg.senderType === "delivery" ? "#13C0A2" : "#fff"
                }}
              >
                <b>{msg.senderType === "user" ? "Customer" : "Delivery Partner"}:</b> {msg.message}
                <span style={{ color: "#aaa", marginLeft: 8, fontSize: 12 }}>
                  {new Date(msg.createdAt).toLocaleString()}
                </span>
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedChat(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// --- DARK THEME ---
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#13C0A2" },
    secondary: { main: "#FFD43B" },
    error: { main: "#e74c3c" },
  },
});

// ------------------------ PAYMENTS TAB PANEL ---------------------------
function PaymentsPayoutsPanel({ token }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchPayments();
    // eslint-disable-next-line
  }, [token]);

  const fetchPayments = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setPayments(data);
    } finally {
      setLoading(false);
    }
  };

  // Helper to safely format all numbers
  const fmt = (v) =>
    (typeof v === "number" ? v : parseFloat(v || "0")).toFixed(2);

  // Payout sum for summary chips
  const payoutSum = (key) =>
    payments.filter((p) => p.status === "paid").reduce((sum, p) => sum + (Number(p[key]) || 0), 0);

  // Show amount in alert if admin clicks chip
  const showFullAmount = (label, amount) => {
    alert(`${label}: ₹${amount}`);
  };

  // Filter for current tab
  const filteredPayments = payments.filter((p) =>
    tab === "all"
      ? true
      : tab === "pharmacy"
      ? !!p.pharmacyId
      : tab === "delivery"
      ? !!p.deliveryPartnerId
      : tab === "admin"
      ? !!p.adminAmount
      : true
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, color: "#FFD43B" }}>
        Payments & Payouts
      </Typography>
      {/* Responsive summary chips row */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mb: 2,
          overflowX: "auto",
          whiteSpace: "nowrap",
          width: "100%",
          pb: 1,
          "& > *": { minWidth: 145, fontSize: { xs: 13, sm: 15 } },
        }}
      >
        <Chip
          label={`Total Collected: ₹${fmt(payoutSum("totalAmount"))}`}
          color="primary"
          onClick={() => showFullAmount("Total Collected", fmt(payoutSum("totalAmount")))}
          sx={{ cursor: "pointer", fontWeight: 700 }}
        />
        <Chip
          label={`To Pharmacy: ₹${fmt(payoutSum("pharmacyAmount"))}`}
          color="success"
          onClick={() => showFullAmount("To Pharmacy", fmt(payoutSum("pharmacyAmount")))}
          sx={{ cursor: "pointer", fontWeight: 700 }}
        />
        <Chip
          label={`To Delivery: ₹${fmt(payoutSum("deliveryAmount"))}`}
          color="secondary"
          onClick={() => showFullAmount("To Delivery", fmt(payoutSum("deliveryAmount")))}
          sx={{ cursor: "pointer", fontWeight: 700 }}
        />
        <Chip
          label={`To Admin: ₹${fmt(payoutSum("adminAmount"))}`}
          color="warning"
          onClick={() => showFullAmount("To Admin", fmt(payoutSum("adminAmount")))}
          sx={{ cursor: "pointer", fontWeight: 700 }}
        />
      </Stack>

      {/* Tab buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
        <Button
          variant={tab === "all" ? "contained" : "outlined"}
          onClick={() => setTab("all")}
        >
          All
        </Button>
        <Button
          variant={tab === "pharmacy" ? "contained" : "outlined"}
          onClick={() => setTab("pharmacy")}
        >
          Pharmacy Payouts
        </Button>
        <Button
          variant={tab === "delivery" ? "contained" : "outlined"}
          onClick={() => setTab("delivery")}
        >
          Delivery Payouts
        </Button>
        <Button
          variant={tab === "admin" ? "contained" : "outlined"}
          onClick={() => setTab("admin")}
        >
          Admin Commission
        </Button>
      </Stack>

      {loading && (
        <Typography sx={{ mb: 2, color: "#FFD43B" }}>Loading...</Typography>
      )}

      <Stack spacing={2}>
        {filteredPayments.map((pay) => (
          <Card
            key={pay._id}
            sx={{
              p: 2,
              bgcolor: "#212325",
              borderRadius: 2,
              mb: 1,
              boxShadow: "0 2px 8px #1115",
            }}
          >
            <Typography fontWeight={700} sx={{ fontSize: 17, color: "#FFD43B" }}>
              Order: #{pay.orderId?._id?.slice(-5) || "NA"}
            </Typography>
            <Typography variant="body2">
              <b>User:</b> {pay.userId?.name || "NA"}
            </Typography>
            <Typography variant="body2">
              <b>Pharmacy:</b> {pay.pharmacyId?.name || "NA"}
            </Typography>
            <Typography variant="body2">
              <b>Delivery Partner:</b> {pay.deliveryPartnerId?.name || "NA"}
            </Typography>
            <Typography sx={{ color: "#13C0A2", fontSize: 15 }}>
              <b>Total Paid:</b> ₹{fmt(pay.totalAmount)}{" "}
              — <b>Pharmacy:</b> ₹{fmt(pay.pharmacyAmount)}{" "}
              — <b>Delivery:</b> ₹{fmt(pay.deliveryAmount)}{" "}
              — <b>Admin:</b> ₹{fmt(pay.adminAmount)}
            </Typography>
            <Typography>Status: <b>{pay.status}</b></Typography>
            <Typography variant="body2" color="text.secondary">
              {pay.createdAt
                ? new Date(pay.createdAt).toLocaleString()
                : ""}
            </Typography>
            {pay.coupon && (
              <Typography variant="body2" color="primary">
                Coupon Used: {pay.coupon.code} (Discount: ₹{fmt(pay.coupon.discount)})
              </Typography>
            )}
            {pay.paymentGatewayDetails && (
              <Typography variant="body2" sx={{ color: "#FFD43B" }}>
                Razorpay: {pay.paymentGatewayDetails.razorpay_payment_id || ""}
              </Typography>
            )}
          </Card>
        ))}
        {filteredPayments.length === 0 && !loading && (
          <Typography>No payments found.</Typography>
        )}
      </Stack>
    </Box>
  );
}

function LabPartnersAdminPanel({ token, onNotify }) {
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [viewMode, setViewMode] = useState("pending");
  const [search, setSearch] = useState("");
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [proposalDrafts, setProposalDrafts] = useState({});
  const [statusBusy, setStatusBusy] = useState("");
  const [proposalBusy, setProposalBusy] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [audit, setAudit] = useState([]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const url = viewMode === "all"
        ? `${API_BASE_URL}/api/lab-partners/admin/all?q=${encodeURIComponent(search)}`
        : `${API_BASE_URL}/api/lab-partners/admin/pending`;
      const { data } = await axios.get(url, { headers });
      const rows = Array.isArray(data?.partners) ? data.partners : [];
      setPartners(rows);
      if (rows.length) {
        if (!selectedPartnerId || !rows.some((r) => r.id === selectedPartnerId)) {
          setSelectedPartnerId(rows[0].id);
          setSelectedPartner(rows[0]);
        } else {
          setSelectedPartner(rows.find((r) => r.id === selectedPartnerId) || null);
        }
      } else {
        setSelectedPartnerId("");
        setSelectedPartner(null);
      }
    } catch {
      onNotify("Failed to load lab partners");
    } finally {
      setLoading(false);
    }
  };

  const loadProposals = async (partnerId) => {
    if (!partnerId) return;
    try {
      setProposalLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/api/lab-partners/admin/${partnerId}/catalog/proposals`, { headers });
      const rows = Array.isArray(data?.proposals) ? data.proposals : [];
      setProposals(rows);
      const draft = {};
      rows.forEach((p) => {
        draft[p.id] = {
          status: p.status || "submitted_for_review",
          adminComment: p.adminComment || "",
          activateCapability: false,
        };
      });
      setProposalDrafts(draft);
    } catch {
      onNotify("Failed to load catalog proposals");
    } finally {
      setProposalLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, viewMode]);

  useEffect(() => {
    if (!token || viewMode !== "all") return;
    const t = setTimeout(() => loadPartners(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (!selectedPartnerId) return;
    loadProposals(selectedPartnerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartnerId]);

  const updatePartnerStatus = async (partnerId, status) => {
    const busyKey = `${partnerId}-${status}`;
    try {
      setStatusBusy(busyKey);
      await axios.patch(
        `${API_BASE_URL}/api/lab-partners/admin/${partnerId}/status`,
        { status },
        { headers }
      );
      onNotify(`Partner status updated: ${status}`);
      setPartners((prev) =>
        prev.map((p) =>
          p.id === partnerId
            ? { ...p, partnerStatus: status, partnerStatusLabel: status.replace(/_/g, " ") }
            : p
        )
      );
    } catch {
      onNotify("Failed to update partner status");
    } finally {
      setStatusBusy("");
    }
  };

  const openDetails = async (partner) => {
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setSelectedPartner(partner);
      const [docsRes, auditRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/lab-partners/admin/${partner.id}/documents`, { headers }),
        axios.get(`${API_BASE_URL}/api/lab-partners/admin/${partner.id}/audit`, { headers }),
      ]);
      setDocs(Array.isArray(docsRes?.data?.documents) ? docsRes.data.documents : []);
      setAudit(Array.isArray(auditRes?.data?.audit) ? auditRes.data.audit : []);
    } catch {
      onNotify("Failed to load partner details");
      setDocs([]);
      setAudit([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const reviewProposal = async (proposalId) => {
    const draft = proposalDrafts[proposalId];
    if (!draft || !selectedPartnerId) return;
    try {
      setProposalBusy(proposalId);
      await axios.patch(
        `${API_BASE_URL}/api/lab-partners/admin/${selectedPartnerId}/catalog/proposals/${proposalId}`,
        {
          status: draft.status,
          adminComment: draft.adminComment,
          activateCapability: !!draft.activateCapability,
        },
        { headers }
      );
      onNotify("Proposal review updated");
      await loadProposals(selectedPartnerId);
    } catch {
      onNotify("Failed to review proposal");
    } finally {
      setProposalBusy("");
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: "#FFD43B" }}>Lab Partners Review</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant={viewMode === "pending" ? "contained" : "outlined"} size="small" onClick={() => setViewMode("pending")}>Pending</Button>
          <Button variant={viewMode === "all" ? "contained" : "outlined"} size="small" onClick={() => setViewMode("all")}>All Partners</Button>
          <Button variant="outlined" size="small" onClick={loadPartners}>Refresh</Button>
        </Stack>
      </Stack>
      {viewMode === "all" && (
        <TextField
          size="small"
          fullWidth
          label="Search by name/org/city/phone/email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />
      )}

      {loading ? (
        <Typography sx={{ color: "#aaa" }}>Loading lab partners...</Typography>
      ) : (
        <Stack spacing={2}>
          {partners.length === 0 && (
            <Typography sx={{ color: "#aaa" }}>No lab partners found for current view.</Typography>
          )}
          {partners.map((p) => (
            <Card key={p.id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
              <Stack spacing={1}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1}>
                  <Box>
                    <Typography fontWeight={700}>{p.name} ({p.organization || "Lab Partner"})</Typography>
                    <Typography variant="body2" sx={{ color: "#aaf" }}>
                      {p.city || "-"} | {p.phone || "-"} | {p.email || "-"}
                    </Typography>
                  </Box>
                  <Chip label={`Status: ${p.partnerStatusLabel || p.partnerStatus || "Under Review"}`} color="warning" />
                </Stack>

                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {[
                    "under_review",
                    "docs_pending",
                    "verification_in_review",
                    "approved",
                    "live",
                    "suspended",
                    "rejected",
                  ].map((st) => (
                    <Button
                      key={st}
                      size="small"
                      variant="outlined"
                      disabled={statusBusy === `${p.id}-${st}`}
                      onClick={() => updatePartnerStatus(p.id, st)}
                    >
                      {st.replace(/_/g, " ")}
                    </Button>
                  ))}
                  <Button
                    size="small"
                    variant={selectedPartnerId === p.id ? "contained" : "outlined"}
                    onClick={() => {
                      setSelectedPartnerId(p.id);
                      setSelectedPartner(p);
                    }}
                  >
                    Review Catalog Proposals
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => openDetails(p)}>
                    Docs & Audit
                  </Button>
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" sx={{ mb: 1, color: "#13C0A2", fontWeight: 700 }}>
        Selected Partner Proposals
      </Typography>
      {proposalLoading ? (
        <Typography sx={{ color: "#aaa" }}>Loading proposals...</Typography>
      ) : (
        <Stack spacing={2}>
          {proposals.length === 0 ? (
            <Typography sx={{ color: "#aaa" }}>No proposals available for selected partner.</Typography>
          ) : (
            proposals.map((pr) => (
              <Card key={pr.id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
                <Stack spacing={1.2}>
                  <Typography fontWeight={700}>{pr.type || "Test"}: {pr.name || "-"}</Typography>
                  <Typography variant="body2" sx={{ color: "#aaf" }}>
                    Category: {pr.category || "-"} | Price: {pr.price || "-"} | TAT: {pr.reportTime || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#bbb" }}>
                    Current: {String(pr.status || "draft").replace(/_/g, " ")} | Admin comment: {pr.adminComment || "-"}
                  </Typography>

                  <FormControl size="small" sx={{ maxWidth: 260 }}>
                    <InputLabel>Review Status</InputLabel>
                    <Select
                      label="Review Status"
                      value={proposalDrafts[pr.id]?.status || "submitted_for_review"}
                      onChange={(e) =>
                        setProposalDrafts((prev) => ({
                          ...prev,
                          [pr.id]: { ...(prev[pr.id] || {}), status: e.target.value },
                        }))
                      }
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="submitted_for_review">Submitted for Review</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="needs_changes">Needs Changes</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    label="Admin Comment"
                    value={proposalDrafts[pr.id]?.adminComment || ""}
                    onChange={(e) =>
                      setProposalDrafts((prev) => ({
                        ...prev,
                        [pr.id]: { ...(prev[pr.id] || {}), adminComment: e.target.value },
                      }))
                    }
                  />

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setProposalDrafts((prev) => ({
                          ...prev,
                          [pr.id]: {
                            ...(prev[pr.id] || {}),
                            activateCapability: !prev[pr.id]?.activateCapability,
                          },
                        }))
                      }
                    >
                      {proposalDrafts[pr.id]?.activateCapability ? "Capability: YES" : "Capability: NO"}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={proposalBusy === pr.id}
                      onClick={() => reviewProposal(pr.id)}
                    >
                      Save Review
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            ))
          )}
        </Stack>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Lab Partner Details: {selectedPartner?.name || "-"}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Typography>Loading details...</Typography>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" sx={{ color: "#FFD43B", fontWeight: 700 }}>
                  Verification Documents ({docs.length})
                </Typography>
                {docs.length === 0 ? (
                  <Typography sx={{ color: "#aaa" }}>No documents uploaded.</Typography>
                ) : (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {docs.map((d, i) => (
                      <Card key={`${d.fileKey || d.fileName || "doc"}-${i}`} sx={{ p: 1.5, bgcolor: "#23272a", borderRadius: 2 }}>
                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                          <Box>
                            <Typography fontWeight={700}>{d.fileName || "Untitled document"}</Typography>
                            <Typography variant="body2" sx={{ color: "#aaf" }}>
                              {d.label || `${d.section || "-"} / ${d.key || "-"}`}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#bbb" }}>
                              {d.mimeType || "-"} | {d.fileSize ? `${Math.round(Number(d.fileSize) / 1024)} KB` : "-"}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            {d.fileUrl ? (
                              <>
                                <Button size="small" variant="outlined" href={d.fileUrl} target="_blank" rel="noreferrer">Preview</Button>
                                <Button size="small" variant="contained" href={d.fileUrl} target="_blank" rel="noreferrer">Download</Button>
                              </>
                            ) : (
                              <Chip size="small" label="No file URL" />
                            )}
                          </Stack>
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ color: "#13C0A2", fontWeight: 700 }}>
                  Admin Audit Timeline ({audit.length})
                </Typography>
                {audit.length === 0 ? (
                  <Typography sx={{ color: "#aaa" }}>No audit entries yet.</Typography>
                ) : (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {audit.map((a, i) => (
                      <Card key={`${a.id || "audit"}-${i}`} sx={{ p: 1.5, bgcolor: "#23272a", borderRadius: 2 }}>
                        <Typography fontWeight={700}>{String(a.action || "-").replace(/_/g, " ")}</Typography>
                        <Typography variant="body2" sx={{ color: "#aaf" }}>
                          {a.notes || "-"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#bbb" }}>
                          Admin: {a.adminId || "system"} | {a.at ? new Date(a.at).toLocaleString() : "-"}
                        </Typography>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function DoctorsVerificationPanel({ token, onNotify }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending_verification");
  const [busy, setBusy] = useState("");
  const [notesByDoctor, setNotesByDoctor] = useState({});
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/api/doctors/admin/all`, {
        headers,
        params: { status: statusFilter },
      });
      setRows(Array.isArray(data?.doctors) ? data.doctors : []);
    } catch {
      onNotify("Failed to load doctors verification queue");
    } finally {
      setLoading(false);
    }
  }, [headers, onNotify, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (doctorId, status) => {
    const key = `${doctorId}-${status}`;
    try {
      setBusy(key);
      await axios.patch(
        `${API_BASE_URL}/api/doctors/admin/${doctorId}/verification-status`,
        { status, note: notesByDoctor[doctorId] || "" },
        { headers }
      );
      onNotify(`Doctor marked ${status}`);
      await load();
    } catch {
      onNotify("Failed to update doctor verification state");
    } finally {
      setBusy("");
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        {["pending_verification", "approved", "rejected", "needs_more_info", "suspended", "all"].map((s) => (
          <Button key={s} size="small" variant={statusFilter === s ? "contained" : "outlined"} onClick={() => setStatusFilter(s)}>
            {s.replace(/_/g, " ")}
          </Button>
        ))}
      </Stack>

      {loading ? <Typography sx={{ color: "#aaa" }}>Loading doctors...</Typography> : null}
      <Stack spacing={2}>
        {!loading && rows.length === 0 ? <Typography sx={{ color: "#aaa" }}>No doctors found.</Typography> : null}
        {rows.map((d) => (
          <Card key={d.id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
            {/* ── Header: Photo + Name + Specialty ── */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              {(d.profilePhotoUrl || d?.documents?.profilePhotoUrl) ? (
                <Box component="img" src={d.profilePhotoUrl || d.documents.profilePhotoUrl} alt="photo"
                  sx={{ width: 48, height: 48, borderRadius: 2, objectFit: "cover", border: "1px solid #444" }} />
              ) : (
                <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: "#333", display: "grid", placeItems: "center", fontSize: 20, color: "#666" }}>
                  👨‍⚕️
                </Box>
              )}
              <Box>
                <Typography fontWeight={900} fontSize={15}>{d.fullName || d.name} {d.gender ? `(${d.gender})` : ""}</Typography>
                <Typography variant="body2" sx={{ color: "#13C0A2", fontWeight: 700 }}>
                  {d.specialty}{d.subSpecialty ? ` → ${d.subSpecialty}` : ""}
                </Typography>
              </Box>
            </Stack>

            {/* ── Contact + Identity ── */}
            <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: "#aaa" }}>📧 {d.email || "-"}</Typography>
              <Typography variant="body2" sx={{ color: "#aaa" }}>📱 {d.phone || "-"}</Typography>
              {d.dateOfBirth && <Typography variant="body2" sx={{ color: "#aaa" }}>🎂 {d.dateOfBirth}</Typography>}
            </Stack>

            {/* ── Professional Details ── */}
            <Typography variant="body2" sx={{ color: "#ccc", mt: 0.5 }}>
              {d.qualification || "-"}{d.university ? ` • ${d.university}` : ""}{d.qualificationYear ? ` (${d.qualificationYear})` : ""}
              {d.yearsExperience ? ` • ${d.yearsExperience} yrs exp` : ""}
            </Typography>
            <Typography variant="body2" sx={{ color: "#ccc" }}>
              📍 {d.area || ""}{d.area && d.city ? ", " : ""}{d.city || "-"}
              {d.languages?.length ? ` • 🗣 ${Array.isArray(d.languages) ? d.languages.join(", ") : d.languages}` : ""}
            </Typography>

            {/* ── NMC / Medical Council ── */}
            <Box sx={{ mt: 1, p: 1, bgcolor: "#1a1e21", borderRadius: 1, border: "1px solid #333" }}>
              <Typography variant="caption" sx={{ color: "#999", fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>
                Medical Registration
              </Typography>
              <Typography variant="body2" sx={{ color: "#eee", mt: 0.3 }}>
                Reg#: <strong>{d?.documents?.registrationNumber || "-"}</strong>
                {(d.smcName || d?.documents?.smcName) ? ` • ${d.smcName || d.documents.smcName}` : ""}
                {(d.registrationYear || d?.documents?.registrationYear) ? ` • Year: ${d.registrationYear || d.documents.registrationYear}` : ""}
              </Typography>
              <Typography variant="body2" sx={{ color: "#eee" }}>
                Status: <strong style={{ color: d.verificationStatus === "approved" ? "#10B981" : d.verificationStatus === "rejected" ? "#EF4444" : "#F59E0B" }}>
                  {String(d.verificationStatus || "pending_verification").replace(/_/g, " ").toUpperCase()}
                </strong>
              </Typography>
            </Box>

            {/* ── Consultation Modes + Fees ── */}
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              {d.consultModes?.audio && (
                <Typography variant="caption" sx={{ bgcolor: "#0C5A3E", color: "#fff", px: 1, py: 0.3, borderRadius: 1, fontWeight: 700 }}>
                  🎧 Audio {d.feeAudio || d.feeCall ? `₹${d.feeAudio || d.feeCall}` : ""}
                </Typography>
              )}
              {d.consultModes?.video && (
                <Typography variant="caption" sx={{ bgcolor: "#0C5A3E", color: "#fff", px: 1, py: 0.3, borderRadius: 1, fontWeight: 700 }}>
                  📹 Video {d.feeVideo ? `₹${d.feeVideo}` : ""}
                </Typography>
              )}
              {d.consultModes?.inPerson && (
                <Typography variant="caption" sx={{ bgcolor: "#0C5A3E", color: "#fff", px: 1, py: 0.3, borderRadius: 1, fontWeight: 700 }}>
                  🏥 In-Person {d.feeInPerson ? `₹${d.feeInPerson}` : ""}
                </Typography>
              )}
            </Stack>

            {/* ── Bank / Payout Details ── */}
            {(d.payoutDetails?.accountHolderName || d.payoutDetails?.bankName) && (
              <Box sx={{ mt: 1, p: 1, bgcolor: "#1a1e21", borderRadius: 1, border: "1px solid #333" }}>
                <Typography variant="caption" sx={{ color: "#999", fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>
                  Bank Details
                </Typography>
                <Typography variant="body2" sx={{ color: "#eee", mt: 0.3 }}>
                  {d.payoutDetails.accountHolderName || "-"} • {d.payoutDetails.bankName || "-"} • IFSC: {d.payoutDetails.ifsc || "-"} • A/C: ****{d.payoutDetails.accountNumberLast4 || d.payoutDetails.accountNumber?.slice(-4) || "-"}
                </Typography>
              </Box>
            )}

            {/* ── Bio ── */}
            {d.bio && (
              <Typography variant="body2" sx={{ color: "#aaa", mt: 1, fontStyle: "italic" }}>
                "{d.bio}"
              </Typography>
            )}

            {/* ── Documents ── */}
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              {[
                ["Reg. Certificate", d?.documents?.registrationCertificateUrl],
                ["MBBS Degree", d?.documents?.mbbsDegreeUrl],
                ["Specialist Degree", d?.documents?.specialistDegreeUrl],
                ["PAN", d?.documents?.panUrl],
                ["Bank Proof", d?.documents?.bankProofUrl],
                ["Clinic Proof", d?.documents?.clinicProofUrl],
                ["Profile Photo", d?.documents?.profilePhotoUrl || d?.profilePhotoUrl],
              ]
                .filter((x) => !!x[1])
                .map(([label, url]) => (
                  <Button key={label} size="small" variant="text" component="a" href={url} target="_blank" rel="noreferrer"
                    sx={{ fontSize: 11, textTransform: "none", color: "#13C0A2" }}>
                    📄 {label}
                  </Button>
                ))}
            </Stack>

            {/* ── Admin Note + Actions ── */}
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 1 }}
              value={notesByDoctor[d.id] || d.verificationNotes || ""}
              onChange={(e) => setNotesByDoctor((p) => ({ ...p, [d.id]: e.target.value }))}
              label="Review note (will be emailed to doctor)"
            />
            <Typography variant="caption" sx={{ color: "#8AA", mt: 0.5 }}>
              Reviewed At: {d?.verificationReviewedAt ? new Date(d.verificationReviewedAt).toLocaleString() : "-"}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              {["approved", "rejected", "needs_more_info", "suspended", "pending_verification"].map((s) => (
                <Button key={s} size="small" variant="outlined" disabled={busy === `${d.id}-${s}`} onClick={() => setStatus(d.id, s)}
                  sx={{
                    fontSize: 11,
                    textTransform: "none",
                    borderColor: s === "approved" ? "#10B981" : s === "rejected" ? "#EF4444" : s === "needs_more_info" ? "#F59E0B" : s === "suspended" ? "#8B5CF6" : "#64748B",
                    color: s === "approved" ? "#10B981" : s === "rejected" ? "#EF4444" : s === "needs_more_info" ? "#F59E0B" : s === "suspended" ? "#8B5CF6" : "#94A3B8",
                    "&:hover": { bgcolor: "rgba(255,255,255,.05)" },
                  }}>
                  {s === "approved" ? "✅ " : s === "rejected" ? "❌ " : s === "needs_more_info" ? "📋 " : s === "suspended" ? "⚠️ " : "⏳ "}
                  {s.replace(/_/g, " ")}
                </Button>
              ))}
            </Stack>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

export default function AdminDashboard() {
  const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [msg, setMsg] = useState("");
  const [stats, setStats] = useState({ orders: 0, users: 0, pharmacies: 0 });
  const [offers, setOffers] = useState([]);
  const [offer, setOffer] = useState({ title: "", description: "", code: "", image: "" });
  const [orderList, setOrderList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [pharmacyList, setPharmacyList] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [pendingPharmacies, setPendingPharmacies] = useState([]);
  const [pendingDeliveryPartners, setPendingDeliveryPartners] = useState([]);
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [deliveryDetailOpen, setDeliveryDetailOpen] = useState(false);
  const [deliveryDetail, setDeliveryDetail] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, partner: null });
  const [cityList, setCityList] = useState([]);
  const [areaList, setAreaList] = useState([]);
  const [city, setCity] = useState("All");
  const [area, setArea] = useState("All");
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationData, setNotificationData] = useState({ title: "", message: "", to: "all" });
  const [medicineModal, setMedicineModal] = useState({ open: false, list: [], pharmacy: null });
  const [showStats, setShowStats] = useState({ open: false, type: "" });
  const [activeTab, setActiveTab] = useState(0);
  const [rxReviewQueue, setRxReviewQueue] = useState([]);
  const [rxAnalytics, setRxAnalytics] = useState(null);
  const [rxQueueBusy, setRxQueueBusy] = useState("");
  const [rxAssignByOrder, setRxAssignByOrder] = useState({});
  // --- Price Override Approvals ---
  const [priceOverrides, setPriceOverrides] = useState([]);
  const [priceOverrideBusy, setPriceOverrideBusy] = useState("");
  // --- ONLY CHANGE: Expanded Order Dialog ---
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      axios.get(`${API_BASE_URL}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setStats(res.data)).catch(() => { });
      axios.get(`${API_BASE_URL}/api/admin/pending-pharmacies`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setPendingPharmacies(res.data)).catch(() => { });
      axios.get(`${API_BASE_URL}/api/offers`).then((res) => setOffers(res.data)).catch(() => { });
      axios.get(`${API_BASE_URL}/api/orders`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setOrderList(res.data)).catch(() => { });
      axios.get(`${API_BASE_URL}/api/users`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUserList(res.data)).catch(() => { });
      axios
        .get(`${API_BASE_URL}/api/admin/pharmacies`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setPharmacyList(res.data))
        .catch(() => { });
      axios.get(`${API_BASE_URL}/api/pharmacies?all=1`).then((res) => {
        setPharmacies(res.data);
        const cities = Array.from(new Set(res.data.map(p => p.city).filter(Boolean)));
        setCityList(["All", ...cities]);
        const filteredPharms = city === "All" ? res.data : res.data.filter(p => p.city === city);
        const areas = Array.from(new Set(filteredPharms.map(p => p.area).filter(Boolean)));
        setAreaList(["All", ...areas]);
      }).catch(() => { });
      axios.get(`${API_BASE_URL}/api/delivery/partners`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setDeliveryPartners(res.data)).catch(() => { });
      axios.get(`${API_BASE_URL}/api/delivery/pending`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setPendingDeliveryPartners(res.data)).catch(() => { });
    }, 3000);
    return () => clearInterval(interval);
  }, [token, city]);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const loadRxOps = async () => {
      try {
        const [queueRes, analyticsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/prescriptions/admin/review-queue`, { headers }),
          axios.get(`${API_BASE_URL}/api/prescriptions/admin/analytics`, { headers }),
        ]);
        setRxReviewQueue(Array.isArray(queueRes?.data?.queue) ? queueRes.data.queue : []);
        setRxAnalytics(analyticsRes?.data || null);
      } catch {
        setRxReviewQueue([]);
      }
    };
    loadRxOps();
    const interval = setInterval(loadRxOps, 8000);
    return () => clearInterval(interval);
  }, [token]);

  // Fetch pending price overrides
  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const load = () => axios.get(`${API_BASE_URL}/api/pharmacies/price-overrides/pending`, { headers })
      .then(r => setPriceOverrides(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [token]);

  const handlePriceOverride = async (id, action, scope) => {
    setPriceOverrideBusy(id);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API_BASE_URL}/api/pharmacies/price-overrides/${id}/${action}`,
        action === "approve" ? { scope } : {},
        { headers }
      );
      setPriceOverrides(prev => prev.filter(o => o._id !== id));
    } catch (e) {
      alert(e?.response?.data?.error || "Failed");
    } finally {
      setPriceOverrideBusy("");
    }
  };

  const handleRxRouteNow = async (order) => {
    if (!order?._id) return;
    setRxQueueBusy(order._id);
    try {
      const payload = {
        items: Array.isArray(order.parsedBasket) ? order.parsedBasket : [],
        routeNow: true,
        mode: order?.modeDecision?.mode || "mode2_partial_exception",
        note: "Admin routed from review queue",
      };
      await axios.put(
        `${API_BASE_URL}/api/prescriptions/admin/review/${order._id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg("Prescription routed to pharmacy.");
    } catch {
      setMsg("Failed to route prescription.");
    } finally {
      setRxQueueBusy("");
    }
  };

  const handleRxAssign = async (orderId) => {
    const pharmacyId = rxAssignByOrder[orderId];
    if (!orderId || !pharmacyId) return;
    setRxQueueBusy(orderId);
    try {
      await axios.put(
        `${API_BASE_URL}/api/prescriptions/admin/assign/${orderId}`,
        { pharmacyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg("Prescription manually assigned.");
    } catch {
      setMsg("Failed to assign prescription.");
    } finally {
      setRxQueueBusy("");
    }
  };

  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/admin/login`, login);
      setToken(res.data.token);
      localStorage.setItem("adminToken", res.data.token);
      setMsg("Logged in as admin!");
    } catch {
      setMsg("Admin login failed.");
    }
  };

  const handleLogout = () => {
    setToken("");
    localStorage.removeItem("adminToken");
    setMsg("Logged out.");
  };

  const approveDeliveryPartner = async (id) => {
    try {
      await axios.post(`${API_BASE_URL}/api/delivery/approve`, { id }, { headers: { Authorization: `Bearer ${token}` } });
      setMsg("Delivery Partner approved!");
      setPendingDeliveryPartners(prev => prev.filter(p => p._id !== id));
    } catch {
      setMsg("Failed to approve delivery partner");
    }
  };

  const handleDeleteDeliveryPartner = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/delivery/delete/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setMsg("Delivery Partner deleted!");
      setDeliveryPartners(prev => prev.filter(p => p._id !== id));
      setDeleteDialog({ open: false, partner: null });
    } catch {
      setMsg("Failed to delete delivery partner");
    }
  };

  const openDeliveryDetail = async (partner) => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/delivery/partner/${partner._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliveryDetail(data);
      setDeliveryDetailOpen(true);
    } catch {
      setMsg("Failed to load delivery partner info");
    }
  };

  const approvePharmacy = async (id) => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/approve-pharmacy`, { pharmacyId: id }, { headers: { Authorization: `Bearer ${token}` } });
      setMsg("Pharmacy approved!");
      setPendingPharmacies((prev) => prev.filter((p) => p._id !== id));
    } catch {
      setMsg("Failed to approve pharmacy");
    }
  };

  const deletePharmacy = async (id) => {
    if (!window.confirm("Are you sure you want to remove this pharmacy?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/pharmacy/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setMsg("Pharmacy deleted!");
      setPharmacies(pharmacies.filter((p) => p._id !== id));
    } catch {
      setMsg("Failed to delete pharmacy");
    }
  };

  const handleViewMedicines = async (pharmacyId, pharmacy) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/medicines?pharmacyId=${pharmacyId}`);
      setMedicineModal({ open: true, list: res.data, pharmacy });
    } catch {
      setMsg("Failed to load medicines");
    }
  };

  const handleDeleteMedicine = async (medId) => {
    if (!window.confirm("Delete this medicine?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/medicines/${medId}`, { headers: { Authorization: `Bearer ${token}` } });
      setMedicineModal((m) => ({ ...m, list: m.list.filter(x => x._id !== medId) }));
      setMsg("Medicine deleted");
    } catch {
      setMsg("Failed to delete medicine");
    }
  };

    // ✅ USER BLOCK/DELETE HELPERS (ADD THIS)
  const isUserBlocked = (u) => !!(u?.isBlocked || u?.blocked || u?.active === false);

  const toggleBlockUser = async (user) => {
    try {
      const blockedNow = isUserBlocked(user);
      const nextBlocked = !blockedNow;

      await axios.patch(
        `${API_BASE_URL}/api/admin/users/${user._id}/block`,
        { blocked: nextBlocked },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUserList((prev) =>
        prev.map((u) =>
          u._id === user._id
            ? { ...u, isBlocked: nextBlocked, blocked: nextBlocked, active: nextBlocked ? false : true }
            : u
        )
      );

      setMsg(nextBlocked ? "User blocked!" : "User unblocked!");
    } catch {
      setMsg("Failed to block/unblock user");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserList((prev) => prev.filter((u) => u._id !== userId));
      setMsg("User deleted!");
    } catch {
      setMsg("Failed to delete user");
    }
  };

  const handleSendNotification = async () => {
    setNotificationOpen(false);
    setNotificationData({ title: "", message: "", to: "all" });
    setMsg(`Notification sent to ${notificationData.to} (demo)`);
  };

  const filteredPharmacies = pharmacies.filter(p =>
    (city === "All" || p.city === city) && (area === "All" || p.area === area)
  );
  const filteredActivePharmacies = filteredPharmacies.filter(p => p.active);
  const activeDeliveryPartners = deliveryPartners.filter(p => p.active);

  if (!token) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Box sx={{ mt: 7, maxWidth: 400, mx: "auto" }}>
          <Typography variant="h5" mb={2}>Admin Login</Typography>
          <TextField label="Email" fullWidth sx={{ mb: 2 }} value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} />
          <TextField label="Password" type="password" fullWidth sx={{ mb: 2 }} value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} />
          <Button variant="contained" fullWidth onClick={handleLogin}>Login</Button>
          <Snackbar open={!!msg} autoHideDuration={2400} onClose={() => setMsg("")}><Alert onClose={() => setMsg("")} severity={msg.includes("fail") ? "error" : "success"}>{msg}</Alert></Snackbar>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ p: 2, maxWidth: 900, mx: "auto" }}>
        <Typography variant="h4" sx={{ fontWeight: 900, color: "#FFD43B", mb: 3 }}>Admin Dashboard</Typography>
        {/* TOP BAR */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2, gap: 2, rowGap: 2, width: "100%", justifyContent: { xs: "flex-start", sm: "flex-start", md: "space-between" } }}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip label={`Total Orders: ${stats.orders}`} color="primary" clickable onClick={() => setShowStats({ open: true, type: "orders" })} sx={{ mb: { xs: 1, sm: 0 } }} />
            <Chip label={`Users: ${stats.users}`} color="secondary" clickable onClick={() => setShowStats({ open: true, type: "users" })} sx={{ mb: { xs: 1, sm: 0 } }} />
            <Chip label={`Pharmacies: ${stats.pharmacies}`} color="success" clickable onClick={() => setShowStats({ open: true, type: "pharmacies" })} sx={{ mb: { xs: 1, sm: 0 } }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="primary" size="small" sx={{ fontWeight: 700 }} startIcon={<NotificationsNone />} onClick={() => setNotificationOpen(true)}>Send Notification</Button>
            <Button variant="outlined" color="error" size="small" sx={{ fontWeight: 700 }} onClick={handleLogout}>Logout</Button>
          </Stack>
        </Stack>
        {/* FILTERS */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel>City</InputLabel>
            <Select label="City" value={city} size="small" onChange={e => { setCity(e.target.value); setArea("All"); }}>
              {cityList.map(cty => (<MenuItem key={cty} value={cty}>{cty}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel>Area</InputLabel>
            <Select label="Area" value={area} size="small" onChange={e => setArea(e.target.value)}>
              {areaList.map(ar => (<MenuItem key={ar} value={ar}>{ar}</MenuItem>))}
            </Select>
          </FormControl>
        </Stack>

        {/* === TABS FOR FAST SWITCHING === */}
        <Box sx={{ mb: 3, bgcolor: "#16181a", borderRadius: 2 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label={`All Orders (${orderList.length})`} />
            <Tab label={`All Users (${userList.length})`} />
            <Tab label={`All Pharmacies (${filteredPharmacies.length})`} />
            <Tab label={`Active Pharmacies (${filteredActivePharmacies.length})`} />
            <Tab label={`Delivery Partners (${deliveryPartners.length})`} />
            <Tab label={`Active Delivery Partners (${activeDeliveryPartners.length})`} />
            <Tab label="Medicine Master" />
<Tab label="Payments & Payouts" />
<Tab label="Lab Partners" />
<Tab label="Doctors Verification" />
<Tab label={`Rx Review (${rxReviewQueue.length})`} />
<Tab label="Control Tower" />
<Tab label="Incidents" />
<Tab label={`Price Overrides${priceOverrides.length ? ` (${priceOverrides.length})` : ""}`} />
          </Tabs>
        </Box>

        {/* === TAB PANELS === */}
        <Box sx={{ mb: 3 }}>
  {activeTab === 0 && (
    <Box>
      <Typography variant="h6" mb={1}>All Orders</Typography>
      {orderList.length === 0 ? (
        <Typography sx={{ color: "#aaa" }}>No orders found.</Typography>
      ) : (
        <Stack spacing={2}>
          {orderList.map(order => (
            <Card key={order._id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
              <Typography fontWeight={700}>Order #{order._id?.slice(-5)}</Typography>
              <Typography variant="body2" sx={{ color: "#aaf" }}>
                User: {order.address?.name} | Pharmacy: {order.pharmacy?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "#FFD43B" }}>
                {order.items?.map(i => `${i.name} x${i.qty || i.quantity || 1}`).join(", ")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#aaa" }}>
                Total: ₹{order.total} | Status: {order.status}
              </Typography>
              <Button
                variant="outlined"
                sx={{ mt: 1 }}
                color="primary"
                size="small"
                onClick={() => setExpandedOrder(order)}
              >
                View Details
              </Button>
            </Card>
          ))}
        </Stack>
      )}

      {/* Expanded Order Details Dialog */}
      <Dialog open={!!expandedOrder} onClose={() => setExpandedOrder(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Order #{expandedOrder?._id?.slice(-5)}</DialogTitle>
        <DialogContent dividers>
          {expandedOrder ? (
            <Stack spacing={1}>
              {/* USER & ADDRESS */}
              <Typography>
                <b>User:</b> {expandedOrder.userId?.name || expandedOrder.address?.name}
                {" "}
                (
                {expandedOrder.userId?.email ||
                  expandedOrder.userId?.mobile ||
                  expandedOrder.address?.mobile ||
                  "NA"}
                )
              </Typography>
              <Typography>
                <b>Address:</b> {expandedOrder.address?.addressLine}, {expandedOrder.address?.city}
                {expandedOrder.address?.pincode && ` - ${expandedOrder.address?.pincode}`}
              </Typography>

              {/* PHARMACY */}
              <Typography>
                <b>Pharmacy:</b> {expandedOrder.pharmacy?.name}
                {expandedOrder.pharmacy?.city && `, ${expandedOrder.pharmacy.city}`}
                {expandedOrder.pharmacy?.area && `, ${expandedOrder.pharmacy.area}`}
                {expandedOrder.pharmacy?.contact && <> ({expandedOrder.pharmacy.contact})</>}
              </Typography>

              {/* DELIVERY PARTNER */}
              <Typography>
                <b>Delivery Partner:</b> {expandedOrder.deliveryPartner?.name || "Not assigned"}
                {expandedOrder.deliveryPartner?.mobile && <> ({expandedOrder.deliveryPartner.mobile})</>}
              </Typography>

              {/* ORDER ITEMS */}
              <Typography><b>Items:</b></Typography>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {expandedOrder.items?.map(i =>
                  <li key={i._id || i.name}>
                    {i.name} x{i.qty || i.quantity || 1}
                    {i.price ? ` — ₹${i.price}` : ""}
                  </li>
                )}
              </ul>

              {/* QUOTE DETAILS */}
              {expandedOrder.quote?.itemsAvailable?.length > 0 && (
                <Typography sx={{ color: "#13C0A2" }}>
                  <b>Available:</b> {expandedOrder.quote.itemsAvailable.map(i => i.name).join(", ")}
                </Typography>
              )}
              {expandedOrder.quote?.itemsUnavailable?.length > 0 && (
                <Typography sx={{ color: "#e74c3c" }}>
                  <b>Unavailable:</b> {expandedOrder.quote.itemsUnavailable.map(i => i.name).join(", ")}
                </Typography>
              )}

              {/* QUOTE AMOUNT & TIME */}
              <Typography>
                <b>Quote:</b> ₹{expandedOrder.quote?.price || "NA"}
                {expandedOrder.quote?.quotedAt && <> (at {new Date(expandedOrder.quote.quotedAt).toLocaleString()})</>}
              </Typography>

              {/* ORDER AMOUNT & PAYMENT STATUS */}
              <Typography><b>Total Amount:</b> ₹{expandedOrder.total}</Typography>
              {expandedOrder.paymentStatus && (
                <Typography><b>Payment Status:</b> {expandedOrder.paymentStatus}</Typography>
              )}

             {/* STATUS TIMELINE */}
<Typography><b>Status Timeline:</b></Typography>
<ul style={{ margin: 0, paddingLeft: 18 }}>
  <li>Placed: {expandedOrder.createdAt ? new Date(expandedOrder.createdAt).toLocaleString() : "—"}</li>
  <li>Pharmacy Quoted: {expandedOrder.quote?.quotedAt ? new Date(expandedOrder.quote.quotedAt).toLocaleString() : "—"}</li>
  <li>
    Pharmacy Accepted: {
      // Try pharmacyAcceptedAt field first
      expandedOrder.pharmacyAcceptedAt
        ? new Date(
            expandedOrder.pharmacyAcceptedAt.$date
              ? expandedOrder.pharmacyAcceptedAt.$date
              : expandedOrder.pharmacyAcceptedAt
          ).toLocaleString()
        // Fallback: look for pharmacy accept event in assignmentHistory
        : (
            expandedOrder.assignmentHistory?.find(h =>
              h.status === "processing" || h.status === "pharmacy_accepted"
            )?.at
            ? new Date(
                expandedOrder.assignmentHistory.find(h =>
                  h.status === "processing" || h.status === "pharmacy_accepted"
                ).at
              ).toLocaleString()
            : "—"
          )
    }
</li>
  <li>User Paid/Confirmed: {expandedOrder.confirmedAt ? new Date(expandedOrder.confirmedAt).toLocaleString() : "—"}</li>

</ul>

{/* DELIVERY ASSIGNMENT HISTORY */}
{expandedOrder.assignmentHistory && expandedOrder.assignmentHistory.length > 0 && (
  <>
    <Typography sx={{ mt: 1 }}><b>Delivery Timeline:</b></Typography>
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {expandedOrder.assignmentHistory.map((h, i) => (
        <li key={i}>
          {h.status.charAt(0).toUpperCase() + h.status.slice(1)}:
          &nbsp;{h.at ? new Date(h.at).toLocaleString() : "—"}
        </li>
      ))}
    </ul>
  </>
)}

              {/* PRESCRIPTION LINK */}
              {expandedOrder.prescriptionUrl && (
                <a
                  href={expandedOrder.prescriptionUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "#FFD43B", marginTop: 8 }}
                >View Prescription</a>
              )}
            </Stack>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpandedOrder(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" mb={1}>All Users</Typography>
              {userList.length === 0 ? (
                <Typography sx={{ color: "#aaa" }}>No users found.</Typography>
              ) : (
                <Stack spacing={2}>
                  {userList.map(user => (
                    <Card key={user._id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
    <Box>
      <Typography fontWeight={700}>{user.name}</Typography>
      <Typography variant="body2" sx={{ color: "#FFD43B" }}>
        {user.email || user.mobile}
      </Typography>
    </Box>

    <Stack direction="row" spacing={1} alignItems="center">
      {isUserBlocked(user) && <Chip size="small" label="Blocked" color="error" />}

      <Button
        size="small"
        variant="outlined"
        color={isUserBlocked(user) ? "success" : "warning"}
        onClick={() => toggleBlockUser(user)}
      >
        {isUserBlocked(user) ? "Unblock" : "Block"}
      </Button>

      <Button
        size="small"
        variant="outlined"
        color="error"
        onClick={() => deleteUser(user._id)}
      >
        Delete
      </Button>
    </Stack>
  </Stack>
</Card>

                  ))}
                </Stack>
              )}
            </Box>
          )}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" mb={1}>All Pharmacies</Typography>
              {filteredPharmacies.length === 0 ? (
                <Typography sx={{ color: "#aaa" }}>No pharmacies found for this filter.</Typography>
              ) : (
                <Stack spacing={2}>
                  {filteredPharmacies.map(pharm => (
                    <Card key={pharm._id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontWeight={700}>{pharm.name}</Typography>
                            <Chip label={pharm.active ? "Active" : "Inactive"} color={pharm.active ? "success" : "default"} size="small" sx={{ ml: 1 }} />
                          </Stack>
                          <Typography variant="body2" sx={{ color: "#aaf", mt: 0.5 }}>{pharm.city}, {pharm.area}</Typography>
                          <Typography variant="body2" sx={{ color: "#bbb" }}>{pharm.contact}</Typography>
                        </Box>
                        <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
                          <IconButton color="primary" onClick={() => alert("Pharmacy Details:\n" + JSON.stringify(pharm, null, 2))}><Visibility /></IconButton>
                          <IconButton color="success" onClick={() => handleViewMedicines(pharm._id, pharm)}><LocalPharmacy /></IconButton>
                          <IconButton color="error" onClick={() => deletePharmacy(pharm._id)}><Delete /></IconButton>
                        </Stack>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" mb={1}>Active Pharmacies</Typography>
              {filteredActivePharmacies.length === 0 ? (
                <Typography sx={{ color: "#aaa" }}>No active pharmacies found for this filter.</Typography>
              ) : (
                <Stack spacing={2}>
                  {filteredActivePharmacies.map(pharm => (
                    <Card key={pharm._id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
                        <Box>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontWeight={700}>{pharm.name}</Typography>
                            <Chip label="Active" color="success" size="small" sx={{ ml: 1 }} />
                          </Stack>
                          <Typography variant="body2" sx={{ color: "#aaf", mt: 0.5 }}>{pharm.city}, {pharm.area}</Typography>
                          <Typography variant="body2" sx={{ color: "#bbb" }}>{pharm.contact}</Typography>
                        </Box>
                        <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
                          <IconButton color="primary" onClick={() => alert("Pharmacy Details:\n" + JSON.stringify(pharm, null, 2))}><Visibility /></IconButton>
                          <IconButton color="success" onClick={() => handleViewMedicines(pharm._id, pharm)}><LocalPharmacy /></IconButton>
                          <IconButton color="error" onClick={() => deletePharmacy(pharm._id)}><Delete /></IconButton>
                        </Stack>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          {activeTab === 4 && (
            <Box>
              <Typography variant="h6" mb={1}>All Delivery Partners</Typography>
              {deliveryPartners.length === 0 ? (
                <Typography sx={{ color: "#aaa" }}>No delivery partners found.</Typography>
              ) : (
                <Stack spacing={2}>
                  {deliveryPartners.map(partner => (
                    <Card key={partner._id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
                        <Box>
                          <Typography fontWeight={700}>
                            <DirectionsRun sx={{ mb: "-5px", mr: 1, color: "#FFD43B" }} />
                            {partner.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#aaf", mt: 0.5 }}>{partner.city}, {partner.area}</Typography>
                          <Typography variant="body2" sx={{ color: "#bbb" }}>{partner.mobile} | {partner.email}</Typography>
                        </Box>
                        <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
                          <IconButton color="primary" onClick={() => openDeliveryDetail(partner)}><Visibility /></IconButton>
                          <IconButton color="error" onClick={() => setDeleteDialog({ open: true, partner })}><Delete /></IconButton>
                        </Stack>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          {activeTab === 5 && (
            <Box>
              <Typography variant="h6" mb={1}>Active Delivery Partners</Typography>
              {activeDeliveryPartners.length === 0 ? (
                <Typography sx={{ color: "#aaa" }}>No active delivery partners found.</Typography>
              ) : (
                <Stack spacing={2}>
                  {activeDeliveryPartners.map(partner => (
                    <Card key={partner._id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
                        <Box>
                          <Typography fontWeight={700}>
                            <DirectionsRun sx={{ mb: "-5px", mr: 1, color: "#FFD43B" }} />
                            {partner.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#aaf", mt: 0.5 }}>{partner.city}, {partner.area}</Typography>
                          <Typography variant="body2" sx={{ color: "#bbb" }}>{partner.mobile} | {partner.email}</Typography>
                        </Box>
                        <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
                          <IconButton color="primary" onClick={() => openDeliveryDetail(partner)}><Visibility /></IconButton>
                          <IconButton color="error" onClick={() => setDeleteDialog({ open: true, partner })}><Delete /></IconButton>
                        </Stack>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          {/* ------------- MEDICINE MASTER TAB ------------- */}
{activeTab === 6 && (
  <MedicineMasterAdmin token={token} />
)}

{/* ------------- PAYMENTS & PAYOUTS TAB ------------- */}
{activeTab === 7 && (
  <PaymentsPayoutsPanel token={token} />
)}
{activeTab === 8 && (
  <LabPartnersAdminPanel token={token} onNotify={setMsg} />
)}
{activeTab === 9 && (
  <DoctorsVerificationPanel token={token} onNotify={setMsg} />
)}
{activeTab === 10 && (
  <Box>
    <Typography variant="h6" mb={1}>Prescription Review Queue</Typography>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
      <Chip
        label={`Queue: ${rxAnalytics?.totals?.reviewQueue ?? rxReviewQueue.length}`}
        color="warning"
      />
      <Chip
        label={`Fill rate: ${rxAnalytics?.totals?.fillRatePercent ?? 0}%`}
        color="success"
      />
      <Chip
        label={`Total Rx Orders: ${rxAnalytics?.totals?.orders ?? 0}`}
        color="primary"
      />
    </Stack>

    {rxReviewQueue.length === 0 ? (
      <Typography sx={{ color: "#aaa" }}>No prescriptions in review queue.</Typography>
    ) : (
      <Stack spacing={2}>
        {rxReviewQueue.map((rx) => (
          <Card key={rx._id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
            <Typography fontWeight={700}>Rx #{rx._id?.slice(-6)}</Typography>
            <Typography variant="body2" sx={{ color: "#aaf", mt: 0.5 }}>
              User: {rx.user?.name || "NA"} | Status: {rx.status || "pending"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#FFD43B", mt: 0.5 }}>
              Mode: {rx?.modeDecision?.mode || "mode3_admin_review"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#bbb", mt: 0.5 }}>
              Basket items: {Array.isArray(rx.parsedBasket) ? rx.parsedBasket.length : 0}
            </Typography>
            <Typography variant="body2" sx={{ color: "#bbb", mt: 0.5 }}>
              Estimated: Rs.{rx?.estimatedPricing?.minTotal || 0} - Rs.{rx?.estimatedPricing?.maxTotal || 0}
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                disabled={rxQueueBusy === rx._id}
                onClick={() => handleRxRouteNow(rx)}
              >
                Route Now
              </Button>
              <FormControl size="small" sx={{ minWidth: 210 }}>
                <InputLabel>Assign pharmacy</InputLabel>
                <Select
                  label="Assign pharmacy"
                  value={rxAssignByOrder[rx._id] || ""}
                  onChange={(e) =>
                    setRxAssignByOrder((prev) => ({
                      ...prev,
                      [rx._id]: e.target.value,
                    }))
                  }
                >
                  {filteredPharmacies.map((ph) => (
                    <MenuItem key={ph._id} value={ph._id}>
                      {ph.name} ({ph.city || "-"})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                disabled={!rxAssignByOrder[rx._id] || rxQueueBusy === rx._id}
                onClick={() => handleRxAssign(rx._id)}
              >
                Assign
              </Button>
            </Stack>
          </Card>
        ))}
      </Stack>
    )}
  </Box>
)}

{/* ------------- CONTROL TOWER TAB ------------- */}
{activeTab === 11 && (
  <AdminControlTower token={token} />
)}

{/* ------------- INCIDENTS TAB ------------- */}
{activeTab === 12 && (
  <AdminIncidentManager token={token} />
)}

{/* ------------- PRICE OVERRIDES TAB ------------- */}
{activeTab === 13 && (
  <Box>
    <Typography variant="h6" mb={2}>Pharmacy Price Override Requests</Typography>
    {priceOverrides.length === 0 ? (
      <Typography sx={{ color: "#aaa" }}>No pending price override requests.</Typography>
    ) : (
      <Stack spacing={2}>
        {priceOverrides.map(ov => {
          const master = ov.medicineMasterId || {};
          const pharmacy = ov.pharmacyId || {};
          return (
            <Card key={ov._id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
              <Stack spacing={1}>
                <Typography fontWeight={700}>{master.name || master.brand || "Unknown Medicine"}</Typography>
                <Typography variant="body2" sx={{ color: "#aaa" }}>
                  {master.brand && `Brand: ${master.brand} · `}{master.composition || ""}
                </Typography>
                <Typography variant="body2" sx={{ color: "#90caf9" }}>
                  Pharmacy: {pharmacy.name || "?"} ({pharmacy.area || pharmacy.city || ""})
                </Typography>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Box>
                    <Typography variant="caption" sx={{ color: "#aaa" }}>Master Price</Typography>
                    <Typography fontWeight={700} sx={{ color: "#4caf50" }}>₹{master.price} (MRP ₹{master.mrp})</Typography>
                  </Box>
                  <Typography sx={{ color: "#FFD43B", fontSize: 20 }}>→</Typography>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#aaa" }}>Requested Price</Typography>
                    <Typography fontWeight={700} sx={{ color: "#ff9800" }}>₹{ov.requestedPrice} (MRP ₹{ov.requestedMrp})</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} mt={1}>
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    disabled={priceOverrideBusy === ov._id}
                    onClick={() => handlePriceOverride(ov._id, "approve", "pharmacy_only")}
                  >
                    Approve (This Pharmacy Only)
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ bgcolor: "#1976d2" }}
                    disabled={priceOverrideBusy === ov._id}
                    onClick={() => handlePriceOverride(ov._id, "approve", "master_catalog")}
                  >
                    Approve & Update Master Catalog
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    disabled={priceOverrideBusy === ov._id}
                    onClick={() => handlePriceOverride(ov._id, "reject")}
                  >
                    Reject
                  </Button>
                </Stack>
              </Stack>
            </Card>
          );
        })}
      </Stack>
    )}
  </Box>
)}

        </Box>

        {/* ALL THE REST OF YOUR PANELS AND MODALS */}
        {/* ORDER MANAGEMENT (Assign Partner), Pending Approvals, Offers, Medicine Modal, Dialogs */}
        {/* --- Order Management --- */}
        <Box sx={{ bgcolor: "#181a1b", borderRadius: 3, p: 2, mb: 3 }}>
          <Typography variant="h6" mb={1}>Order Management (Assign Delivery Partner)</Typography>
          {orderList.filter(o => o.status === "processing").length === 0 && (
            <Typography sx={{ color: "#aaa" }}>No processing orders to assign.</Typography>
          )}
          <Stack spacing={2}>
            {orderList.filter(o => o.status === "processing").map(order => (
              <Card key={order._id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Box>
                    <Typography fontWeight={700}>Order #{order._id?.slice(-5)}</Typography>
                    <Typography variant="body2" sx={{ color: "#aaf", mt: 0.5 }}>
                      User: {order.address?.name} | Pharmacy: {order.pharmacy?.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#FFD43B" }}>
                      {order.items?.map(i => `${i.name} x${i.qty || i.quantity || 1}`).join(", ")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#aaa" }}>
                      Total: ₹{order.total}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 220, ml: "auto" }}>
                    {!order.deliveryPartner ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <InputLabel>Assign Partner</InputLabel>
                          <Select
                            label="Assign Partner"
                            value={order.assigningPartnerId || ""}
                            onChange={e => {
                              const val = e.target.value;
                              setOrderList(l =>
                                l.map(oo =>
                                  oo._id === order._id ? { ...oo, assigningPartnerId: val } : oo
                                )
                              );
                            }}
                          >
                            {deliveryPartners.filter(dp => dp.active).map(dp => (
                              <MenuItem key={dp._id} value={dp._id}>{dp.name} ({dp.mobile})</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={async () => {
                            const partnerId = order.assigningPartnerId;
                            if (!partnerId) return alert("Select delivery partner");
                            try {
                              await axios.patch(
                                `${API_BASE_URL}/api/orders/${order._id}/assign-delivery-partner`,
                                { deliveryPartnerId: partnerId },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              setMsg("Delivery partner assigned!");
                              setOrderList(l =>
                                l.map(oo =>
                                  oo._id === order._id
                                    ? { ...oo, deliveryPartner: deliveryPartners.find(dp => dp._id === partnerId) }
                                    : oo
                                )
                              );
                            } catch {
                              setMsg("Failed to assign partner!");
                            }
                          }}
                        >Assign</Button>
                      </Stack>
                    ) : (
                      <Chip color="success" label={`Assigned: ${order.deliveryPartner?.name || "Partner"}`} sx={{ fontWeight: 600 }} />
                    )}
                  </Box>
                </Stack>
              </Card>
            ))}
          </Stack>
        </Box>
        {/* PENDING PHARMACY APPROVAL */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: "#FFD43B" }}>Pending Pharmacies</Typography>
          {pendingPharmacies.length === 0 ? (
            <Typography sx={{ color: "#aaa", mb: 2 }}>No pending pharmacies.</Typography>
          ) : (
            pendingPharmacies.map((pharm) => (
              <Box
  key={pharm._id}
  sx={{
    my: 1,
    p: 2,
    bgcolor: "#23272a",
    borderRadius: 2,
    mb: 2,
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "flex-start", sm: "center" },
    justifyContent: "space-between",
  }}
>
  <Box sx={{ minWidth: 0 }}>
    <span>
      <b>{pharm.name}</b> ({pharm.area}, {pharm.city}) – {pharm.contact}
    </span>
    {/* Documents preview for admin review */}
    <Box sx={{ mt: 1 }}>
      {[
        { label: "Qualification Cert", key: "qualificationCert" },
        { label: "Council Cert", key: "councilCert" },
        { label: "Retail License", key: "retailLicense" },
        { label: "Wholesale License", key: "wholesaleLicense" },
        { label: "GST Cert", key: "gstCert" },
        { label: "Shop Establishment", key: "shopEstablishmentCert" },
        { label: "Trade License", key: "tradeLicense" },
        { label: "Identity Proof", key: "identityProof" },
        { label: "Address Proof", key: "addressProof" },
        { label: "Digital Signature", key: "digitalSignature" },
      ].map(doc => (
        pharm[doc.key] && (
          <div key={doc.key}>
            <span style={{ color: "#FFD43B", fontWeight: 600 }}>{doc.label}: </span>
            {pharm[doc.key].endsWith(".jpg") ||
            pharm[doc.key].endsWith(".jpeg") ||
            pharm[doc.key].endsWith(".png") ? (
              <img
                src={`https://api.godavaii.com${pharm[doc.key]}`}
                alt={doc.label}
                style={{ maxWidth: 120, margin: "6px 0", borderRadius: 6, display: "block" }}
              />
            ) : (
              <a
                href={`https://api.godavaii.com${pharm[doc.key]}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#FFD43B" }}
              >
                View Document
              </a>
            )}
          </div>
        )
      ))}
      {pharm.photo && (
        <div>
          <span style={{ color: "#FFD43B", fontWeight: 600 }}>Photo: </span>
          <img
            src={`https://api.godavaii.com${pharm.photo}`}
            style={{ maxWidth: 120, borderRadius: 8, margin: "6px 0" }}
            alt="Pharmacist"
          />
        </div>
      )}
    </Box>
  </Box>
  <Button
    size="small"
    variant="contained"
    color="success"
    sx={{ mt: { xs: 2, sm: 0 } }}
    onClick={() => approvePharmacy(pharm._id)}
  >
    Approve
  </Button>
</Box>
            ))
          )}
        </Box>
        {/* PENDING DELIVERY PARTNER APPROVAL */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: "#FFD43B" }}>Pending Delivery Partners</Typography>
          {pendingDeliveryPartners.length === 0 ? (
            <Typography sx={{ color: "#aaa", mb: 2 }}>No pending delivery partners.</Typography>
          ) : (
            pendingDeliveryPartners.map((dp) => (
              <Box key={dp._id} sx={{ my: 1, p: 2, bgcolor: "#23272a", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span><b>{dp.name}</b> ({dp.area}, {dp.city}) – {dp.mobile}</span>
                <Button size="small" variant="contained" color="success" onClick={() => approveDeliveryPartner(dp._id)}>Approve</Button>
              </Box>
            ))
          )}
        </Box>
        {/* ALL DELIVERY PARTNERS */}
        <Box sx={{ bgcolor: "#181a1b", borderRadius: 3, p: 2, mb: 3 }}>
          <Typography variant="h6" mb={1}>All Delivery Partners</Typography>
          {deliveryPartners.length === 0 && <Typography sx={{ color: "#aaa" }}>No delivery partners found.</Typography>}
          <Stack spacing={2}>
            {deliveryPartners.map(partner => (
              <Card key={partner._id} sx={{ p: 2, bgcolor: "#23272a", borderRadius: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
                  <Box>
                    <Typography fontWeight={700}>
                      <DirectionsRun sx={{ mb: "-5px", mr: 1, color: "#FFD43B" }} />
                      {partner.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#aaf", mt: 0.5 }}>{partner.city}, {partner.area}</Typography>
                    <Typography variant="body2" sx={{ color: "#bbb" }}>{partner.mobile} | {partner.email}</Typography>
                  </Box>
                  <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
                    <IconButton color="primary" onClick={() => openDeliveryDetail(partner)}><Visibility /></IconButton>
                    <IconButton color="error" onClick={() => setDeleteDialog({ open: true, partner })}><Delete /></IconButton>
                  </Stack>
                </Stack>
              </Card>
            ))}
          </Stack>
        </Box>

        {/* --- Delivery Partner Detail Modal --- */}
        <Dialog open={deliveryDetailOpen} onClose={() => setDeliveryDetailOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Delivery Partner Info</DialogTitle>
          <DialogContent dividers>
            {deliveryDetail ? (
              <Box>
                <Typography fontWeight={700} fontSize={18}>{deliveryDetail.partner.name}</Typography>
                <Typography sx={{ color: "#bbb" }}>{deliveryDetail.partner.mobile} | {deliveryDetail.partner.email}</Typography>
                <Typography>Vehicle: {deliveryDetail.partner.vehicle}</Typography>
                <Typography>City/Area: {deliveryDetail.partner.city}, {deliveryDetail.partner.area}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography fontWeight={700}>Bank:</Typography>
                <Typography>Account Holder: {deliveryDetail.partner.bankDetails.accountHolder}</Typography>
                <Typography>Acc: {deliveryDetail.partner.bankDetails.bankAccount}, IFSC: {deliveryDetail.partner.bankDetails.ifsc}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography fontWeight={700}>Aadhaar: {deliveryDetail.partner.aadhaarNumber}</Typography>
                <a href={`/${deliveryDetail.partner.aadhaarDocUrl}`} target="_blank" rel="noreferrer">Aadhaar Document</a>
                <Typography fontWeight={700}>PAN: {deliveryDetail.partner.panNumber}</Typography>
                <a href={`/${deliveryDetail.partner.panDocUrl}`} target="_blank" rel="noreferrer">PAN Document</a>
                <Divider sx={{ my: 1 }} />
                <Typography fontWeight={700}>Current Active Order</Typography>
                {deliveryDetail.activeOrder ? (
                  <Box sx={{ my: 1 }}>
                    <Typography>Order #{deliveryDetail.activeOrder._id?.slice(-5)} | {deliveryDetail.activeOrder.status}</Typography>
                    <Typography>Pharmacy: {deliveryDetail.activeOrder.pharmacy?.name}</Typography>
                    <Typography>Customer: {deliveryDetail.activeOrder.address?.name}</Typography>
                    <Typography>Address: {deliveryDetail.activeOrder.address?.addressLine}</Typography>
                    {deliveryDetail.activeOrder.driverLocation?.lat && deliveryDetail.activeOrder.driverLocation?.lng && (
                      <img
                        alt="map"
                        src={`https://maps.googleapis.com/maps/api/staticmap?center=${deliveryDetail.activeOrder.driverLocation.lat},${deliveryDetail.activeOrder.driverLocation.lng}&zoom=15&size=400x180&markers=color:red%7C${deliveryDetail.activeOrder.driverLocation.lat},${deliveryDetail.activeOrder.driverLocation.lng}&key=AIzaSyCwztiOU2rdeyoNNDDoM4nQzMrG2pPuTTA`}
                        style={{ width: "100%", borderRadius: 6, marginTop: 8 }}
                      />
                    )}
                  </Box>
                ) : (
                  <Typography color="error">No active orders</Typography>
                )}
                <Divider sx={{ my: 1 }} />
                <Typography fontWeight={700}>Past Orders</Typography>
                {deliveryDetail.pastOrders && deliveryDetail.pastOrders.length > 0 ? (
                  <Stack spacing={1} sx={{ maxHeight: 150, overflowY: "auto" }}>
                    {deliveryDetail.pastOrders.map(o => (
                      <Box key={o._id} sx={{ p: 1, borderBottom: "1px solid #333" }}>
                        #{o._id?.slice(-5)} — ₹{o.total} — {o.status} — {o.createdAt?.slice(0, 10)}
                      </Box>
                    ))}
                  </Stack>
                ) : <Typography color="error">No past orders</Typography>}
              </Box>
            ) : <Typography>Loading...</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeliveryDetailOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        {/* --- Delivery Partner Delete Dialog --- */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, partner: null })}>
          <DialogTitle>Delete/Discontinue Delivery Partner</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to remove this delivery partner? This cannot be undone.</Typography>
            <Typography fontWeight={700} color="error">{deleteDialog.partner?.name}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, partner: null })}>Cancel</Button>
            <Button variant="contained" color="error" onClick={() => handleDeleteDeliveryPartner(deleteDialog.partner?._id)}>Delete</Button>
          </DialogActions>
        </Dialog>
        {/* --- Offers Management --- */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6">Add Offer (Coupon)</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
            <TextField label="Title" value={offer.title} onChange={(e) => setOffer({ ...offer, title: e.target.value })} />
            <TextField label="Description" value={offer.description} onChange={(e) => setOffer({ ...offer, description: e.target.value })} />
            <TextField label="Code" value={offer.code} onChange={(e) => setOffer({ ...offer, code: e.target.value })} />
            <TextField label="Image URL" value={offer.image} onChange={(e) => setOffer({ ...offer, image: e.target.value })} />
            <Button variant="contained" onClick={() => {
              axios.post(`${API_BASE_URL}/api/admin/offer`, offer, { headers: { Authorization: `Bearer ${token}` } })
                .then(() => { setMsg("Offer added!"); setOffer({ title: "", description: "", code: "", image: "" }); })
                .catch(() => setMsg("Failed to add offer"));
            }}>
              Add
            </Button>
          </Stack>
        </Box>
        <Typography variant="h6" mb={1}>All Offers</Typography>
        {offers.map((ofr) => (
          <Card key={ofr._id} sx={{ mb: 2, bgcolor: "#16181a" }}>
            <CardContent>
              <Typography variant="subtitle1">{ofr.title}</Typography>
              <Typography variant="body2">{ofr.description}</Typography>
              <Typography variant="body2" color="text.secondary">{ofr.code}</Typography>
              {ofr.image && <img src={ofr.image} alt="" style={{ maxWidth: 120, marginTop: 6, borderRadius: 8 }} />}
            </CardContent>
          </Card>
        ))}
        {/* --- Medicine Modal --- */}
        <Dialog open={medicineModal.open} onClose={() => setMedicineModal({ open: false, list: [], pharmacy: null })} maxWidth="sm" fullWidth>
          <DialogTitle>
            Medicines {medicineModal.pharmacy ? `for ${medicineModal.pharmacy.name}` : ""}
          </DialogTitle>
          <DialogContent dividers>
            {medicineModal.list.length === 0 && (<Typography>No medicines found.</Typography>)}
            <Stack spacing={2}>
              {medicineModal.list.map(med => (
                <Card key={med._id} sx={{ p: 2, bgcolor: "#212325" }}>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography fontWeight={700}>{med.name}</Typography>
                      <Typography variant="body2" sx={{ color: "#FFD43B" }}>₹{med.price}</Typography>
                      <Typography variant="body2" sx={{ color: "#aaf" }}>Stock: {med.stock}</Typography>
                    </Box>
                    <IconButton color="error" onClick={() => handleDeleteMedicine(med._id)}><Delete /></IconButton>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMedicineModal({ open: false, list: [], pharmacy: null })}>Close</Button>
          </DialogActions>
        </Dialog>
        {/* --- Notification Modal --- */}
        <Dialog open={notificationOpen} onClose={() => setNotificationOpen(false)}>
          <DialogTitle>Send Notification</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <FormControl>
                <InputLabel>Recipient</InputLabel>
                <Select
                  label="Recipient"
                  value={notificationData.to}
                  onChange={e => setNotificationData({ ...notificationData, to: e.target.value })}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  <MenuItem value="users">Users Only</MenuItem>
                  <MenuItem value="pharmacists">Pharmacists Only</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Title" value={notificationData.title} onChange={e => setNotificationData({ ...notificationData, title: e.target.value })} fullWidth />
              <TextField label="Message" value={notificationData.message} onChange={e => setNotificationData({ ...notificationData, message: e.target.value })} fullWidth multiline minRows={2} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNotificationOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSendNotification}>Send</Button>
          </DialogActions>
        </Dialog>
        {/* --- Stats Modal --- */}
        <Dialog open={showStats.open} onClose={() => setShowStats({ open: false, type: "" })} maxWidth="sm" fullWidth>
          <DialogTitle>
            {showStats.type === "orders" && "All Orders"}
            {showStats.type === "users" && "All Users"}
            {showStats.type === "pharmacies" && "All Pharmacies"}
          </DialogTitle>
          <DialogContent dividers sx={{ maxHeight: 400 }}>
            {showStats.type === "orders" && (
              <Stack spacing={1}>
                {orderList.map(order => (
                  <Box key={order._id} sx={{ p: 1, borderBottom: "1px solid #333" }}>
                    #{order._id?.slice(-5)} — ₹{order.total} — {order.status} — {order.createdAt?.slice(0, 10)}
                  </Box>
                ))}
              </Stack>
            )}
            {showStats.type === "users" && (
              <Stack spacing={1}>
                {userList.map(user => (
                  <Box key={user._id} sx={{ p: 1, borderBottom: "1px solid #333" }}>
                    {user.name} — {user.email || user.mobile}
                  </Box>
                ))}
              </Stack>
            )}
            {showStats.type === "pharmacies" && (
              <Stack spacing={1}>
                {pharmacyList.map(pharm => (
                  <Box key={pharm._id} sx={{ p: 1, borderBottom: "1px solid #333" }}>
                    {pharm.name} ({pharm.city}, {pharm.area}) — {pharm.contact}
                  </Box>
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowStats({ open: false, type: "" })}>Close</Button>
          </DialogActions>
        </Dialog>
        {/* --- Support Chats Panel (BOTTOM) --- */}
        <SupportChatsAdminPanel />
        <DeliveryPartnerChatsAdminPanel />
        <Snackbar open={!!msg} autoHideDuration={2400} onClose={() => setMsg("")}>
          <Alert onClose={() => setMsg("")} severity={msg.includes("fail") ? "error" : "success"}>{msg}</Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
